import { A } from '@adornis/base/env-info.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { buildEmail } from '@adornis/mails/db/templates.js';
import { html, nothing } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { LogistikShippingRecipient } from '../enums/LogistikEnums.js';
import type { LogistikInfoMail } from '../LogistikInfoMail.js';
import type { LogistikOrder } from '../LogistikOrder.js';
import { g_getProductCategoryString, type LogistikProduct } from '../LogistikProduct.js';
import { baseMail } from './mailTemplates.js';
import { logistikIcons, logistikLogoImageRef } from './server/mailer.js';

function getLogistikBaseMail() {
  if (!baseMail) throw new Error('Basemail not initalized');
  return {
    ...baseMail,
    get footer() {
      return baseMail?.footer ?? '';
    },
    get logoConfig() {
      return { src: A.absoluteUrl(logistikLogoImageRef), alt: 'CHANCENlogistik Logo', width: 200 };
    },
    customStyle: `${baseMail.customStyle ?? ''} tr.logo td { padding: 20px; }`,
  };
}

export const digitalDelivery = ({ productTitle, pdfID }: { productTitle: string; pdfID: string }) => {
  const fileLink = AdornisFile.getDownloadLink(pdfID);
  return {
    subject: `Versand von ${productTitle} (DIGITAL)`,
    html: buildEmail({
      ...getLogistikBaseMail(),
      title: '',
      contents: html`
        <p>Vielen Dank für Ihre Bestellung!</p>
        <br />
        <p>Mit dieser Email erhalten Sie einen Download-Link zu Ihrer CHANCENheft-Bestellung.</p>

        <br />
        <p>Klicken Sie ganz einfach auf den <a href="${fileLink}">Link zum Download</a>.</p>

        <br />
        <p>Alternativ können Sie folgende URL in die Suchleiste Ihres Browsers eingeben:</p>

        <br />
        ${fileLink}

        <br />
        <br />
        <p>
          Viele Grüße,<br />
          Ihr CHANCENlogistik-Team
        </p>
      `,
    }),
  };
};

export const selfPrintDelivery = ({
  productTitle,
  generatedLink,
}: {
  productTitle: string;
  generatedLink: string;
}) => ({
  subject: `Versand von ${productTitle} (SELBSTDRUCK)`,
  html: buildEmail({
    ...getLogistikBaseMail(),
    title: '',
    contents: html`
      <p>Vielen Dank für Ihre Bestellung!</p>
      <br />
      <p>Mit dieser Email erhalten Sie einen Download-Link zu Ihrer CHANCENbrief-Bestellung.</p>
      <br />
      <p>Klicken Sie ganz einfach auf den <a href="${generatedLink}">Link zum Download</a>.</p>
      <br />
      <p>Alternativ können Sie folgende URL in die Suchleiste Ihres Browsers eingeben:</p>
      <br />
      ${generatedLink}
      <br /><br />
      <p>
        Viele Grüße,
        <br />
        Ihr CHANCENlogistik-Team
      </p>
    `,
  }),
});

export const newCustomEmail = ({ title, content }: { title: string; content: string }) => {
  return buildEmail({ ...getLogistikBaseMail(), title, contents: html`${unsafeHTML(content)}` });
};

export const newOrderInformation = ({ order }: { order: LogistikOrder }) => {
  return buildEmail({
    ...getLogistikBaseMail(),
    title: 'Neue Bestellung bei CHANCENlogistik',
    contents: html`Es wurde soeben eine neue Bestellung aufgegeben.
      <br />
      <br />
      Viele Grüße, Ihr CHANCENlogistik-Team`,
  });
};

export const orderStatusUpdateNew = ({ order }: { order: LogistikOrder }) => {
  return buildEmail({
    ...getLogistikBaseMail(),
    title: 'Wir haben Ihre Bestellung erhalten',
    contents: html`
      <p style="text-align: center;">
        <img
          src="${A.absoluteUrl(logistikIcons.newReleases)}"
          style="margin-left: auto; margin-right: auto; height: 50px;"
        />
        <br />
        <br />
        <b> Neue Bestellung </b>
      </p>
      <br />
      <p>Wir haben soeben Ihre Bestellung erhalten und werden diese schnellstmöglich bearbeiten.</p>
      <br />
      <p>
        Sehen Sie sich <a href="${A.absoluteUrl(`/order/status/${order._id}`)}"> hier </a> den aktuellen Status Ihrer
        Bestellung an.
      </p>
      <p>
        An gleicher Stelle finden Sie auch bei Verfügbarkeit Downloads für die Lösungshefte und Handreichungen der
        bestellten CHANCENhefte.
      </p>
      <br />
      <br />
      <p>
        Viele Grüße,<br />
        Ihr CHANCENlogistik-Team
      </p>
    `,
  });
};

export const orderStatusUpdateProblem = ({ order }: { order: LogistikOrder }) => {
  return buildEmail({
    ...getLogistikBaseMail(),
    title: 'Es gibt ein Problem mit Ihrer Bestellung',
    contents: html`
      <p style="text-align: center;">
        <img
          src="${A.absoluteUrl(logistikIcons.warning)}"
          style="margin-left: auto; margin-right: auto; height: 50px;"
        />
        <br />
        <br />
        <b> Problem bei der Bestellung </b>
      </p>
      <br />
      <p>Es gibt ein Problem mit Ihrer Bestellung.</p>
      <br />
      <p>
        Sehen Sie sich <a href="${A.absoluteUrl(`/order/status/${order._id}`)}"> hier </a> den aktuellen Status Ihrer
        Bestellung an.
      </p>
      <br />
      <br />
      <p>
        Viele Grüße,<br />
        Ihr CHANCENlogistik-Team
      </p>
    `,
  });
};

export const orderStatusUpdateProcessing = ({ order }: { order: LogistikOrder }) => {
  return buildEmail({
    ...getLogistikBaseMail(),
    title: 'Ihre Bestellung wird bearbeitet',
    contents: html`
      <p style="text-align: center;">
        <img
          src="${A.absoluteUrl(logistikIcons.hourglass)}"
          style="margin-left: auto; margin-right: auto; height: 50px;"
        />
        <br />
        <br />
        <b> Bestellung in Bearbeitung </b>
      </p>
      <br />
      <p>Ihre Bestellung wird gerade bearbeitet.</p>
      <br />
      <br />
      <p>
        Sehen Sie sich <a href="${A.absoluteUrl(`/order/status/${order._id}`)}"> hier </a> den aktuellen Status Ihrer
        Bestellung an.
      </p>
      <br />
      <br />
      <p>
        Viele Grüße,<br />
        Ihr CHANCENlogistik-Team
      </p>
    `,
  });
};

export const orderStatusUpdateShipped = ({ order }: { order: LogistikOrder }) => {
  return buildEmail({
    ...getLogistikBaseMail(),
    title: 'Ihre Bestellung wurde versendet',
    contents: html`
      <p style="text-align: center;">
        <img
          src="${A.absoluteUrl(logistikIcons.localShipping)}"
          style="margin-left: auto; margin-right: auto; height: 50px;"
        />
        <br />
        <br />
        <b> Bestellung wurde versendet </b>
      </p>
      <br />
      <p>Ihre Bestellung wurde versendet.</p>
      <br />
      <br />
      <p>
        Sehen Sie sich <a href="${A.absoluteUrl(`/order/status/${order._id}`)}"> hier </a> den aktuellen Status Ihrer
        Bestellung an.
      </p>
      <br />
      <br />
      <p>
        Viele Grüße,<br />
        Ihr CHANCENlogistik-Team
      </p>
    `,
  });
};

export const orderDeliveryStatusRequest = ({ order }: { order: LogistikOrder }) => {
  return buildEmail({
    ...getLogistikBaseMail(),
    title: 'Haben Sie Ihre Bestellung erhalten?',
    contents: html`
      <p>Haben Sie Ihre Bestellung erhalten?</p>
      <br />
      <br />
      <p>
        Bestätigen Sie <a href="${A.absoluteUrl(`/order/status/${order._id}`)}"> hier </a> den Erhalt Ihrer Bestellung.
      </p>
      <br />
      <br />
      <p>
        Viele Grüße,<br />
        Ihr CHANCENlogistik-Team
      </p>
    `,
  });
};

export const heaufigenBestellungenMail = ({
  customerEmail,
  count,
  product,
}: {
  customerEmail: string;
  count: number;
  product: { title: string };
}) => {
  return buildEmail({
    ...getLogistikBaseMail(),
    title: 'Meldungen bei häufigen Bestellungen',
    contents: html`
      <p>Meldungen bei häufigen Bestellungen</p>
      <br />
      <br />
      <p>
        wir möchten Sie darauf hinweisen, dass der Kunde mit der E-Mail-Adresse ${customerEmail} innerhalb der letzten 8
        Wochen ${count} Mal das Produkt <strong>${product.title}</strong> bestellt hat.
      </p>
      <br />
      <br />
      <p>
        Viele Grüße,<br />
        Ihr CHANCENlogistik-Team
      </p>
    `,
  });
};

export const productQuantityWarning = ({ product }: { product: LogistikProduct }) => {
  return buildEmail({
    ...getLogistikBaseMail(),
    title: `Warnung: Produkt "${product.title}" hat nur noch ${product.availableQuantity} Stück auf Lager`,
    contents: html`
      <p style="text-align: center;">
        <img
          src="${A.absoluteUrl(logistikIcons.warning)}"
          style="margin-left: auto; margin-right: auto; height: 50px;"
        />
        <br />
        <br />
        <b> Produkt Stückzahl Warnung </b>
      </p>
      <p>Warnung: Produkt "${product.title}" hat nur noch ${product.availableQuantity} Stück auf Lager</p>
      <br />
      <br />
      <p>
        Viele Grüße,<br />
        Ihr CHANCENlogistik-Team
      </p>
    `,
  });
};

export const newClearanceNeeded = () =>
  buildEmail({
    ...getLogistikBaseMail(),
    title: 'Neue Freigabe erwartet',
    contents: html`
      Sehr geehrte/r Mitarbeiter:in,
      <br />
      <br />
      eine neue individuelle Bestellung wartet auf Ihre Freigabe. Sie können die Freigabe über
      <a href="${A.absoluteUrl('/admin/terminal')}"> diesen Link </a> tätigen.
      <br />
      <br />
      Viele Grüße
      <br />
      <p>
        Viele Grüße,<br />
        Ihr CHANCENlogistik-Team
      </p>
    `,
  });

export const orderApproved = () =>
  buildEmail({
    ...getLogistikBaseMail(),
    title: 'Deine individuelle Bestellung wurde freigegeben',
    contents: html`
      <p style="text-align: center;">
        <img
          src="${A.absoluteUrl(logistikIcons.checkCircle)}"
          style="margin-left: auto; margin-right: auto; height: 50px;"
        />
        <br />
        <br />
        <b> Bestellung freigegeben </b>
      </p>
      <p>Ihre Bestellung wurde freigegeben.</p>
      <br />
      <br />
      <p>
        Viele Grüße,<br />
        Ihr CHANCENlogistik-Team
      </p>
    `,
  });

export const orderDeclined = (order: LogistikOrder) =>
  buildEmail({
    ...getLogistikBaseMail(),
    title: 'Deine individuelle Bestellung wurde abgelehnt',
    contents: html`
      <p style="text-align: center;">
        <img
          src="${A.absoluteUrl(logistikIcons.warning)}"
          style="margin-left: auto; margin-right: auto; height: 50px;"
        />
        <br />
        <br />
        <b> Bestellung abgelehnt </b>
      </p>
      <p>Ihre Bestellung wurde abgelehnt.</p>
      <br />
      <br />
      ${order.has('clearanceNote') && order.clearanceNote
        ? html`
            <p>
              Ihre Bestellung wurde ${order.has('clearanceFrom') ? `von ${order.clearanceFrom}` : ''} abgelehnt mit
              folgender Begründung: <br />
              ${unsafeHTML(order.clearanceNote)}
            </p>
            <br />
            <br />
          `
        : ''}
      ${order.has('clearanceAlternatives') && order.clearanceAlternatives
        ? html`
            <p>
              Sie können Ihre Bestellung <a href=${A.absoluteUrl(`/order/status/${order._id}`)}> hier </a> anschauen und
              eine vorgeschlagene Alternative akzeptieren.
            </p>
            <br />
            <br />
          `
        : ''}
      <p>
        Viele Grüße,<br />
        Ihr CHANCENlogistik-Team
      </p>
    `,
  });

export const newProductsInfoMail = (infoMail: LogistikInfoMail) =>
  buildEmail({
    ...getLogistikBaseMail(),
    title: 'Neue Produkte in der CHANCENlogistik',
    contents: html`
      ${infoMail.bannerFileID
        ? html`
            <img
              src=${AdornisFile.getServeLink(infoMail.bannerFileID)}
              style="width:calc(100% + 40px); max-width:calc(100% + 40px); margin: -20px -20px 0 -20px;"
            />
          `
        : nothing}
      <p>${unsafeHTML(infoMail.introText)}</p>
      <br />
      <div style="background: #eee; height: 1px; width: 100%;"></div>
      <br />
      <p>
        ${(infoMail.products?.length ?? 0) > 1
          ? html`
              <span> <b> Produkte </b> </span>
              <br />
            `
          : nothing}
        ${(infoMail.products ?? []).map(
          (product, i) => html`
            <a
              href="${A.absoluteUrl(`/order/item/${product._id}`)}"
              style="text-decoration: none; color: inherit; width: 100%;"
            >
              <div style="display: table; border-radius: 15px; border: 1px solid #eee; overflow: hidden; width: 100%;">
                <div
                  style="display: table-cell; background: url('${AdornisFile.getServeLink(
                    product.imageID,
                  )}'); background-size: cover; background-repeat: no-repeat; background-position: center; height: 75px; width: 75px;"
                ></div>
                <div style="display: table-cell; vertical-align: middle; padding: 16px;">
                  <b> ${product.title} </b> <br />
                  <span style="color: #666666;"> ${g_getProductCategoryString(product._class)} </span>
                </div>
              </div>
            </a>
            ${i < infoMail.productIDs.length - 1 ? html` <br /> ` : nothing}
          `,
        )}
        <br />
        <br />
        <a href="${A.absoluteUrl('/')}"> Hier durch alle Produkte stöbern </a>
      </p>
      <div style="background: #eee; height: 1px; width: 100%;"></div>
      <br />
      <p>${unsafeHTML(infoMail.outroText)}</p>
      <p>
        Viele Grüße,<br />
        Ihr CHANCENlogistik-Team
      </p>
    `,
  });

export const mergeOrderInfoMail = (order: LogistikOrder) =>
  buildEmail({
    ...getLogistikBaseMail(),
    title: 'Bestellung wurde zusammengelegt',
    contents: html`
      <p style="text-align: center;">
        <img src="${A.absoluteUrl(logistikIcons.merge)}" style="margin-left: auto; margin-right: auto; height: 50px;" />
        <br />
        <br />
        <b> Bestellung zusammengelegt </b>
      </p>
      <br />
      <br />
      <p>Ihre Bestellung wurde mit anderen Bestellungen zusammengelegt.</p>
      <br />
      <p>
        <b> Folgende Bestellungen wurden zusammengefasst </b> <br />
        <br />
        ${(order.mergeOriginalOrders ?? []).map(
          (originalOrder, i) => html`
            <div style="display: table; border-radius: 15px; border: 1px solid #eee; overflow: hidden; width: 100%;">
              <div style="display: table-cell; vertical-align: middle; padding: 16px;">
                <b> ${originalOrder.orderNumber} </b> <br />
              </div>
            </div>
            ${i < (order.mergeOriginalOrders ?? []).length - 1 ? html` <br /> ` : nothing}
          `,
        )}
      </p>
      <p>
        <b> Die Lieferadresse der zusammengefassten Bestellung ist: </b> <br />
        <br />
        ${order.shippingDetails.shippingRecipient} <br />
        ${order.shippingDetails.shippingRecipient === LogistikShippingRecipient.SCHOOL
          ? html`
              ${order.shippingDetails.shippingSchool?.address?.strasse ?? '-'} <br />
              ${order.shippingDetails.shippingSchool?.address?.zipCity?.zip ?? '-'}
              ${order.shippingDetails.shippingSchool?.address?.zipCity?.city ?? '-'}
            `
          : html`
              ${order.shippingDetails.shippingRecipientName ?? 'nicht angegeben'} <br />
              ${order.shippingDetails.shippingAddress?.toString()}
              <br />
            `} <br />
        ${order.shippingDetails.customerEmail} <br />
      </p>
      <br />
      <br />
      <p>
        Sie können den Status Ihrer zusammengefassten Bestellung
        <a href=${A.absoluteUrl(`/order/status/${order._id}`)}> hier </a> anschauen.
      </p>
      <p>
        Viele Grüße,<br />
        Ihr CHANCENlogistik-Team
      </p>
    `,
  });
